// ============================================================================
// useProposerInter — action « Proposer un groupe Inter » (Phase 3, 2026-08-06)
// ============================================================================
// @systeme: cycle-plateforme-inter
//
// UNE SEULE logique pour lancer une proposition de groupe marketplace, quelle
// que soit l'org active :
// - le contexte actif porte la permission INTER.groupe.proposer → navigation
//   directe vers l'entrée du parcours ;
// - sinon, si l'utilisateur a un membership sur l'org marketplace (créé à la
//   validation de sa candidature animateur Inter), on BASCULE d'abord — même
//   mécanique que le sélecteur d'org (POST /auth/switch-org, P8) — puis on
//   navigue.
// Consommateurs : QuickActionsFAB (action contextuelle) et
// espace-animateur/MesGroupesPage (bouton « Proposer un groupe »).
// Le bouton propose, le serveur autorise — les permissions sont revérifiées
// à destination.
//
// DESTINATION CHANGÉE le 2026-08-19 (chantier « pile d'animateurs ») : le
// parcours commence par un positionnement sur un axe et non plus par le
// formulaire complet, qui ne s'ouvre qu'une fois un créneau attribué et
// refuse de s'afficher sans. Lot 10.7 (N235) : la destination est « Mes
// propositions » (/mon-espace/animation/propositions), dont l'onglet par
// défaut dit les deux portes — se positionner sur un axe, ou proposer un
// groupe privé hors catalogue.
// ============================================================================

import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useOrganisation } from "../contexts/OrganisationContext";
import { useCan } from "./usePermissions";

export function useProposerInter(): {
  /** L'action a un sens pour cet utilisateur (habilité, ou animateur avec la permission). */
  visible: boolean;
  /** L'action passera par une bascule d'org avant de naviguer. */
  necessiteBascule: boolean;
  /** Bascule si nécessaire, puis ouvre l'entrée du parcours de proposition. */
  lancerProposition: () => Promise<void>;
} {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { switchMembership } = useOrganisation();
  const canProposer = useCan("INTER.groupe.proposer");

  const plateformeActif = user?.plateformeActif ?? false;
  const membershipInter = user?.memberships?.find((m) => m.organisationType === "INTER");
  const visible = plateformeActif || ((user?.estAnimateur ?? false) && canProposer);
  const necessiteBascule = !canProposer && !!membershipInter;

  async function lancerProposition(): Promise<void> {
    if (necessiteBascule && membershipInter) {
      try {
        await switchMembership(membershipInter.id);
      } catch {
        toast.error("Impossible de basculer sur la plateforme Inter");
        return;
      }
    }
    navigate("/mon-espace/animation/propositions");
  }

  return { visible, necessiteBascule, lancerProposition };
}

export default useProposerInter;
